import {
  ComponentType,
  forwardRef,
  RefObject,
  useEffect,
  useState,
} from 'react';
import ReactPlayer from 'react-player';
import { canPlay } from 'react-player/patterns';
import { VideoElementProps } from 'react-player/types';
import { Loader } from '@designsystem/stories/02-atoms/Loader';
import YouTubePlayer from './YouTubePlayer';

interface EmbedProps {
  style?: React.CSSProperties;
  className?: string;
  src: string;
  title?: string;
  preload: string;
  controls: boolean;
  muted: boolean;
  autoPlay: boolean;
  loop: boolean;
  playsInline: boolean;
  disableRemotePlayback: boolean;
  config: any;
  fallback?: React.ReactNode;
  light?:
    | string
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  playIcon: React.ReactNode;
  onLoadStart: () => void;
  onPlay: () => void;
}

const SwitchTubePlayer = forwardRef<HTMLIFrameElement, VideoElementProps>(
  ({ src, autoPlay, controls, muted, ...props }, ref) => {
    const url = [
      `${src}?`,
      `muted=${muted ? 'true' : 'false'}`,
      `&autoplay=${autoPlay ? 'true' : 'false'}`,
      controls ? '' : '&title=hide',
    ].join('');

    return (
      <iframe
        ref={ref as RefObject<HTMLIFrameElement>}
        src={url}
        frameBorder="0"
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
        title={props.title || 'SwitchTube video player'}
      />
    );
  },
);

if (ReactPlayer.addCustomPlayer) {
  ReactPlayer.addCustomPlayer({
    key: 'switchtube',
    name: 'SwitchTube',
    canPlay: (url: string) => /^https:\/\/tube\.switch\.ch\/.*/.test(url),
    player: SwitchTubePlayer as ComponentType<VideoElementProps>,
  });
  // Same element and same `config.youtube` as react-player's own YouTube
  // player — it only fixes the order in which the two reach it (#1710).
  // Custom players are matched first, so this shadows the built-in one for
  // exactly the URLs react-player would have routed to it.
  ReactPlayer.addCustomPlayer({
    key: 'youtube',
    name: 'YouTube',
    canPlay: canPlay.youtube,
    player: YouTubePlayer as ComponentType<VideoElementProps>,
  });
}

const Embed = (playerProps: EmbedProps) => {
  const [render, doRender] = useState(false);

  useEffect(() => {
    if (!render) {
      // Embed videos can cause hydration issues, so we delay the rendering
      // until after the component has mounted
      doRender(true);
    }
  }, [render]);

  if (!render) {
    return (
      <div className="placeholder placeholder--skeleton">
        <Loader />
      </div>
    );
  }

  return <ReactPlayer {...playerProps} />;
};

export default Embed;
